import axios from "axios";
import { getAuthInstance } from "@tailormed/common-client/services/auth";
import { oktaConfig } from "../constant/okta";
import { authHeader } from "@tailormed/common-client/services/auth";

export const SERVER_URL = `${window.location.origin.replace("4900", "1776")}/`;
const API_VERSION_1 = "api/v1/";
const USERS = "users/";
const INSIGHTS = "insights/";

console.log("----------------------------------");
console.log("VERSION:", process.env.REACT_APP_VERSION);
console.log("SERVER_URL:", SERVER_URL);
console.log("----------------------------------");

axios.defaults.withCredentials = true;

const ApiMap = {};
const ApiCallerMap = {};

const CallerType = Object.freeze({
  Generic: 0,
  Auth: 1
});

const ApiNames = Object.freeze({
  Login: "Login",
  LoggedUser: "LoggedUser",
  Settings: "Settings",
  Providers: "Providers",
  Assignees: "Assignees",
  InsightsEnrollmentsReport: "InsightsEnrollmentsReport",
  InsightsEnrollmentClaims: "InsightsEnrollmentClaims",
  InsightsClaims: "InsightsClaims",
  UserGroups: "UserGroups",
  InsightsOrders: "InsightsOrders",
  InsightsCompletedOrders: "InsightsCompletedOrders"
});

function getAccessToken() {
  const oktaAuth = getAuthInstance(oktaConfig.redirectUri);
  return oktaAuth.getAccessToken();
}

async function axiosWithAuthentication(config) {
  const token = getAccessToken();
  config.headers = authHeader(token);
  return axios(config);
}

const add = (name, action, caller = CallerType.Generic) => {
  Object.assign(ApiCallerMap, { [name]: caller });
  Object.assign(ApiMap, { [name]: (...args) => action(...args) });
};

const addAuth = (name, action) => add(name, action, CallerType.Auth);

add(ApiNames.Login, (username, password) => {
  const data = new FormData();
  data.append("username", username);
  data.append("password", password);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}token/frontend`,
    data
  };
});

addAuth(ApiNames.Assignees, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}${USERS}assignable`,
  headers: authHeader(token)
}));

addAuth(ApiNames.LoggedUser, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}${USERS}logged-user/insights`,
  headers: authHeader(token)
}));

addAuth(ApiNames.Settings, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}settings`,
  headers: authHeader(token)
}));

const getLoginImagesAPI = () =>
  axios({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}app/login-images/frontend`
  });

const getOktaClientIdApi = () =>
  axios({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}app/okta-client-id`
  });

const sendSupportTicketAPI = (subject, comment, user) => {
  const data = {
    ticket: {
      subject,
      comment,
      application: "Insights",
      user
    }
  };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}support/create-ticket`,
    data
  });
};

const postPortalDataThroughChromeExtension = (data) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}accelerated-enrollment-bot/reportPortalData`,
    data
  });
};

addAuth(ApiNames.Providers, (token, providerType) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}providers`,
  params: { type: providerType, skipFacilityFilter: "true" },
  headers: authHeader(token)
}));

addAuth(ApiNames.InsightsEnrollmentsReport, (token, filters) => ({
  method: "POST",
  url: `${SERVER_URL}${API_VERSION_1}${INSIGHTS}report/insightsEnrollments`,
  data: filters,
  headers: authHeader(token),
  responseType: "blob"
}));

addAuth(ApiNames.InsightsEnrollmentClaims, (token, filters) => ({
  method: "POST",
  url: `${SERVER_URL}${API_VERSION_1}${INSIGHTS}report/insightsEnrollmentClaims`,
  data: filters,
  headers: authHeader(token),
  responseType: "blob"
}));

addAuth(ApiNames.InsightsClaims, (token, filters) => ({
  method: "POST",
  url: `${SERVER_URL}${API_VERSION_1}${INSIGHTS}report/insightsClaims`,
  data: filters,
  headers: authHeader(token),
  responseType: "blob"
}));

addAuth(ApiNames.InsightsOrders, (token, filters) => ({
  method: "POST",
  url: `${SERVER_URL}${API_VERSION_1}${INSIGHTS}report/insightsOrders`,
  data: filters,
  headers: authHeader(token),
  responseType: "blob"
}));

addAuth(ApiNames.InsightsCompletedOrders, (token, filters) => ({
  method: "POST",
  url: `${SERVER_URL}${API_VERSION_1}${INSIGHTS}report/insightsCompletedOrders`,
  data: filters,
  headers: authHeader(token),
  responseType: "blob"
}));

const api = (methodName) => {
  const action = ApiMap[methodName];
  return (...params) =>
    new Promise((resolve, reject) => {
      const req = action(...params);
      axios(req)
        .then((res) => resolve(res.data))
        .catch((error) => reject(Object.assign(error, { methodName })));
    });
};

addAuth(ApiNames.UserGroups, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}userGroups`,
  headers: authHeader(token)
}));

export {
  api,
  ApiNames,
  ApiMap,
  ApiCallerMap,
  CallerType,
  getLoginImagesAPI,
  getOktaClientIdApi,
  postPortalDataThroughChromeExtension,
  sendSupportTicketAPI
};
